/**
 * ArticlesPage - Browse and search SEND advice articles
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Icons } from '../../lib/Icons';

// Article category configuration
const CATEGORIES = [
  { 
    id: 'all', 
    label: 'All Articles', 
    icon: Icons.BookOpen,
    color: 'from-slate-500 to-slate-600',
    bgColor: 'bg-slate-500/10',
    iconColor: 'text-slate-400'
  },
  { 
    id: 'strategies', 
    label: 'Classroom Strategies', 
    icon: Icons.Puzzle,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10',
    iconColor: 'text-blue-400'
  },
  { 
    id: 'statutory', 
    label: 'Statutory Processes', 
    icon: Icons.ClipboardDocumentList,
    color: 'from-purple-500 to-violet-500',
    bgColor: 'bg-purple-500/10',
    iconColor: 'text-purple-400'
  },
  { 
    id: 'parent-communication', 
    label: 'Parent Communication', 
    icon: Icons.ChatBubble,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10',
    iconColor: 'text-green-400'
  },
  { 
    id: 'specific-needs', 
    label: 'Specific Needs', 
    icon: Icons.Heart,
    color: 'from-rose-500 to-pink-500',
    bgColor: 'bg-rose-500/10',
    iconColor: 'text-rose-400'
  },
  { 
    id: 'wellbeing', 
    label: 'Wellbeing', 
    icon: Icons.Star,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500/10',
    iconColor: 'text-amber-400'
  },
];

const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    loadArticles();
  }, [selectedCategory]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const listArticles = httpsCallable(functions, 'listSensiArticlesFunc');
      
      const category = selectedCategory !== 'all' ? selectedCategory : undefined;
      const result = await listArticles({ category });
      
      setArticles(result.data || []);
      setSearchResults(null);
    } catch (error) {
      console.error('Error loading articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const searchArticles = httpsCallable(functions, 'searchSensiArticlesFunc');
      
      const result = await searchArticles({ 
        query: searchQuery,
        limit: 20 
      });
      
      setSearchResults(result.data || []);
    } catch (error) {
      console.error('Error searching articles:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleCategoryChange = (categoryId) => {
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
    clearSearch();
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  const displayedArticles = searchResults !== null ? searchResults : articles;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link
                to="/solo/dashboard"
                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Icons.ChevronLeft className="w-5 h-5 text-slate-400" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Icons.BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Advice Hub</h1>
                  <p className="text-xs text-slate-400">SEND guidance & strategies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/50 via-slate-900 to-purple-900/30"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Evidence-Based SEND Guidance
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            In-depth articles to help you support pupils with special educational needs
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles... (e.g., 'EBSA', 'annual review', 'PDA')"
                className="w-full px-5 py-4 pl-12 bg-slate-800/80 backdrop-blur-sm text-white placeholder-slate-400 rounded-2xl border border-slate-700 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none"
              />
              <Icons.Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-16 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white"
                >
                  <Icons.XMark className="w-5 h-5" />
                </button>
              )}
              <button
                type="submit"
                disabled={searching}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
              >
                {searching ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Search'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-6 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map(category => {
              const CategoryIcon = category.icon;
              const isSelected = selectedCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                    isSelected
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <CategoryIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Results header */}
          {searchResults !== null && (
            <div className="mb-6 flex items-center justify-between">
              <p className="text-slate-400">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
              <button
                onClick={clearSearch}
                className="text-sm text-violet-400 hover:text-violet-300"
              >
                Clear search
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500/20 border-t-violet-500" />
            </div>
          ) : displayedArticles.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-800 flex items-center justify-center">
                <Icons.BookOpen className="w-8 h-8 text-slate-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No articles found</h3>
              <p className="text-slate-400 mb-4">
                {searchResults !== null 
                  ? 'Try a different search term'
                  : 'Articles will appear here once seeded'}
              </p>
              {searchResults !== null && (
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-xl transition-colors"
                >
                  Browse all articles
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedArticles.map(article => {
                const categoryInfo = getCategoryInfo(article.category);
                const CategoryIcon = categoryInfo.icon;
                
                return (
                  <Link
                    key={article.slug}
                    to={`/solo/articles/${article.slug}`}
                    className="group bg-slate-800 rounded-2xl overflow-hidden hover:ring-2 hover:ring-violet-500/50 transition-all"
                  >
                    {/* Article header with category gradient */}
                    <div className={`h-24 bg-gradient-to-br ${categoryInfo.color} p-4 flex items-end relative`}>
                      <div className="absolute top-3 right-3 w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <CategoryIcon className="w-5 h-5 text-white" />
                      </div>
                      <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                        {categoryInfo.label}
                      </span>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-violet-400 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-3 mb-4">
                        {article.summary}
                      </p>

                      {/* Meta info */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {article.readingTimeMinutes && (
                          <span className="flex items-center gap-1">
                            <Icons.Clock className="w-3 h-3" />
                            {article.readingTimeMinutes} min read
                          </span>
                        )}
                        {article.sources?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Icons.Link className="w-3 h-3" />
                            {article.sources.length} source{article.sources.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {article.tags?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1">
                          {article.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-slate-700/50 text-slate-400 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {article.tags.length > 3 && (
                            <span className="px-2 py-0.5 text-slate-500 text-xs">
                              +{article.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Background decorations */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-64 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-64 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default ArticlesPage;
